import { Inject, Injectable, InjectionToken, NgZone } from '@angular/core';
import { StorefrontService } from '../services/storefront.service';
import { PREVIEW, PREVIEW_PROTOCOL, PreviewMode, isTrustedParent } from './preview-mode';

/** The preview request this page booted with (a token so tests can supply their own). */
export const PREVIEW_MODE = new InjectionToken<PreviewMode>('PREVIEW_MODE', { providedIn: 'root', factory: () => PREVIEW });

/**
 * The storefront's half of the admin live preview (see preview-mode.ts).
 *
 * Started from an APP_INITIALIZER (without holding up boot). Once GET /api/v1/brand has answered,
 * the `parent` origin from the URL is verified against the brand's `adminOrigin` (fallback:
 * environment.previewParentOrigins):
 *  - verified → tell the admin `ready`, which makes it send the current draft; after that it
 *    pushes the draft again on every edit;
 *  - rejected → tell the parent `rejected` (so the editor can show a clear error) and ignore it.
 * Each draft is the `storefront` block of GET /api/v1/brand (presentational keys only, already
 * localized for this preview's language) and is applied over the published config, so menu,
 * branches and offers stay real.
 */
@Injectable({ providedIn: 'root' })
export class PreviewService {
  private started = false;
  /** The parent origin once verified — the only origin messages are accepted from / posted to. */
  private trustedOrigin: string | null = null;

  constructor(
    private sf: StorefrontService,
    private zone: NgZone,
    @Inject(PREVIEW_MODE) private mode: PreviewMode,
  ) {}

  get active(): boolean { return this.mode.active; }

  /** Begin the handshake. Resolves once the parent has been verified (or rejected). */
  start(): Promise<void> {
    if (!this.mode.active || this.started) return Promise.resolve();
    this.started = true;

    window.addEventListener('message', (event: MessageEvent) => {
      // Only our own parent window, only from the exact verified admin origin.
      if (!this.trustedOrigin || event.source !== window.parent || event.origin !== this.trustedOrigin) return;

      const msg = event.data;
      if (!msg || msg.protocol !== PREVIEW_PROTOCOL) return;

      if (msg.type === 'draft' && msg.storefront && typeof msg.storefront === 'object') {
        // Applied now, or held until the published config has loaded.
        this.zone.run(() => this.sf.applyDraft(msg.storefront));
        this.post('applied');
      }
    });

    // The brand's adminOrigin comes with GET /api/v1/brand — verify once it has answered.
    return this.sf.whenLoaded().then(() => this.verify());
  }

  private verify(): void {
    const parent = this.mode.parentOrigin;
    if (parent && isTrustedParent(parent, this.sf.adminOrigin)) {
      this.trustedOrigin = parent;
      this.post('ready');
      return;
    }
    // Rejected. Tell the frame's parent so the editor can show a clear error. Posted only to the
    // origin the URL named (never `*`): a real admin with a misconfigured origin receives it, a
    // page that forged `parent` doesn't. The message carries nothing but the rejection.
    if (parent) window.parent.postMessage({ protocol: PREVIEW_PROTOCOL, type: 'rejected' }, parent);
  }

  private post(type: string): void {
    if (!this.trustedOrigin) return;
    window.parent.postMessage({ protocol: PREVIEW_PROTOCOL, type }, this.trustedOrigin);
  }
}
